import { ActivatedRouteSnapshot } from "@angular/router";
import { AlertDefaults, AlertType, IncidentEvent, Person, PriorityType, SingleAlertDefaultsRequest, Template } from "src/app/hickory-api"
import { deepCopy, isValidIntegerId, toInteger } from "src/app/shared/model/utility";

export class AlertConfig {
  static readonly TEMPLATE_ID = 'templateId';
  static readonly RESEND_ALERT_ID = 'resendAlertId';
  static readonly RELATED_ALERT_ID = 'relatedAlertId';
  static readonly RELATED_POLL_QUESTION_ID = 'pollQuestionId';
  static readonly RELATED_POLL_OPTION_ID = 'pollOptionId';
  static readonly TRIGGER_VANILLA = 'vanilla';

  // alert stuff
  template: Template;
  alertType: AlertType;
  priorityType: PriorityType;
  get alert(): AlertDefaults { return this.template?.alertSettings; }
  get templateId(): number { return this.template?.id; }

  incidentId: string;
  incidentEvent: IncidentEvent;

  // rarf stuff
  relatedAlertId: number;
  relatedType: SingleAlertDefaultsRequest.RelatedTypeEnum;
  relatedPollQuestionId: number;
  relatedPollOptionId: number;

  // other stuff
  trigger: string;
  editTemplate: boolean = false
  editScheduled: boolean = false
  resendAlertId: number;

  // call center (uses legacy impersonation)
  callCenterReporter: Person;
  get callCenter(): boolean { return !!this.callCenterReporter; }

  constructor(template?: Template, alertType?: AlertType, trigger?: string) {
    this.template = template;
    this.alertType = this.alert?.alertType ?? template?.alertType ?? alertType ?? AlertType.Default;
    this.trigger = trigger;
  }

  static fromAlertType(type: AlertType = AlertType.Default): AlertConfig {
    const config: AlertConfig = new AlertConfig();
    config.alertType = type;
    return config;
  }

  static fromEditTemplate(edit: boolean) {
    const config: AlertConfig = new AlertConfig();
    config.editTemplate = edit;
    return config;
  }

  static fromCallCenter(reporter: Person): AlertConfig {
    const config: AlertConfig = new AlertConfig();
    config.alertType = AlertType.Default;
    config.callCenterReporter = reporter;

    return config;
  }

  static fromRoute(route: ActivatedRouteSnapshot): AlertConfig {
    const params: { [key: string]: string; } = {};
    Object.assign(params, deepCopy(route?.queryParams));
    const segments = route?.url?.map(s => s.path.toLowerCase());

    // intuit the templateId
    const templateId = this.getIntegerId(params, this.TEMPLATE_ID);
    const config: AlertConfig = isValidIntegerId(templateId) ? new AlertConfig({ id: templateId }) : new AlertConfig();

    // intuit the alertType -- this can be in the data, the query params, or we can figure it out from the route path
    const alertTypeString = params.type ?? (segments?.includes('poll') ? 'poll' : 'default');
    config.alertType = alertTypeString === 'poll' ? AlertType.Poll : AlertType.Default;

    // rarf alert
    config.relatedAlertId = this.getIntegerId(params, this.RELATED_ALERT_ID);
    config.relatedPollQuestionId = this.getIntegerId(params, this.RELATED_POLL_QUESTION_ID);
    config.relatedPollOptionId = this.getIntegerId(params, this.RELATED_POLL_OPTION_ID);
    if (params.relatedType) config.relatedType = SingleAlertDefaultsRequest.RelatedTypeEnum[params.relatedType];
    if (isValidIntegerId(config.relatedAlertId)) config.alertType = AlertType.Default; // rarf is always type Default

    // resend alert & edit scheduled
    config.resendAlertId = this.getIntegerId(params, this.RESEND_ALERT_ID);
    config.editScheduled = params.editScheduled?.toLowerCase() === 'true';
    
    return config;
  }

  private static getIntegerId(params: { [key: string]: string; }, key: string): number {
    if (!params || !key) return undefined;
    const value = toInteger(params[key]);
    return (isValidIntegerId(value)) ? value : undefined;
  }
}